import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { publishedAtMs } from 'components/Reports/utils/partitionPublishedSitreps';

const IN_PROGRESS = new Set(['DRAFT', 'PENDING', 'REJECTED']);

const dateMs = (value: string | Date | null | undefined) => {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export const reportScopeKey = (sitrep: GetSitrepsResponse) =>
  sitrep.incident_id || sitrep.aor_id;

/** A working copy: draft, waiting on review, or sent back. */
export function isInProgressSitrep(sitrep: GetSitrepsResponse) {
  return (
    sitrep.approval_status != null && IN_PROGRESS.has(sitrep.approval_status)
  );
}

/**
 * An approved version, including a legacy published row that predates approval
 * status. Those are the report equivalent of a signed form version.
 */
export function isApprovedSitrep(sitrep: GetSitrepsResponse) {
  if (isInProgressSitrep(sitrep)) return false;
  return (
    sitrep.approval_status === 'APPROVED' || sitrep.approval_status == null
  );
}

const versionTime = (sitrep: GetSitrepsResponse) =>
  dateMs(sitrep.updated_at) ||
  dateMs(sitrep.timestamp) ||
  publishedAtMs(sitrep);

export type ReportChain = {
  key: string;
  /** Oldest first. The versions dialog reverses this so the newest is on top. */
  versions: GetSitrepsResponse[];
  /** The open draft when one exists, otherwise the latest approved report. */
  tip: GetSitrepsResponse;
  inProgress: GetSitrepsResponse | null;
};

/**
 * One chain per incident or area. Later copies of the same id win, so pass
 * pending reviews after drafts.
 */
export function buildReportChains(
  sitreps: GetSitrepsResponse[]
): ReportChain[] {
  const byId = new Map<string, GetSitrepsResponse>();
  for (const sitrep of sitreps) {
    byId.set(sitrep.id, sitrep);
  }

  const groups = new Map<string, GetSitrepsResponse[]>();
  for (const sitrep of byId.values()) {
    const key = reportScopeKey(sitrep);
    if (!key) continue;
    const list = groups.get(key) ?? [];
    list.push(sitrep);
    groups.set(key, list);
  }

  const chains: ReportChain[] = [];
  for (const [key, members] of groups) {
    const versions = [...members].sort(
      (a, b) => versionTime(a) - versionTime(b)
    );
    const active = versions.filter((sitrep) => !sitrep.archived);
    const pool = active.length > 0 ? active : versions;
    const inProgress =
      [...pool]
        .filter(isInProgressSitrep)
        .sort((a, b) => versionTime(b) - versionTime(a))[0] ?? null;
    const latestApproved =
      [...pool]
        .filter(isApprovedSitrep)
        .sort((a, b) => publishedAtMs(b) - publishedAtMs(a))[0] ?? null;
    const tip = inProgress ?? latestApproved ?? pool[pool.length - 1];
    chains.push({ key, versions, tip, inProgress });
  }

  chains.sort((a, b) => a.tip.name.localeCompare(b.tip.name));
  return chains;
}
